import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Box, Button, Fade, FormHelperText, Typography } from '@mui/material';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { CommonError, handleError } from '@/utils/errorUtils';
import { CircularProgress } from '@altirllc/shared/storybook/stories/CircularProgress.stories';

import { GenericModal } from '@/components/modal/GenericModal';
import { ReservedLines } from '@/components/reservedLines/ReservedLines';

import { styles } from './UploadImage.styles';
import { UploadImagePlaceholder } from './UploadImagePlaceholder';
import { UploadImageSelected } from './UploadImageSelected';

type TUploadImageProps = TModalsContext & {
  title: string;
  save: (image: File) => Promise<void>;
  /**
   * Optional. When given, the caller owns the failure message (for example a toast): a failed
   * `save` is passed here and the modal shows no inline error and does not log. The modal still
   * stays open with Save enabled so the operator can retry. When omitted (Customer page), the
   * modal shows its own inline error and logs unknown errors, as before.
   */
  onSaveError?: (error: unknown) => void;
};

export const UploadImage = ({ close, title, save, lock, onSaveError }: TUploadImageProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    multiple: false,
    maxSize: 1024 * 1024,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },

    onDropAccepted: useCallback((files) => {
      setError(null);
      setFile(files[0]);
    }, []),

    onDropRejected: useCallback(
      (files) => {
        setFile(null);

        switch (files[0].errors[0]?.code) {
          case 'file-invalid-type':
            setError(t('uploadImage.invalidType'));
            break;

          case 'file-too-large':
            setError(t('uploadImage.tooLarge'));
            break;

          case 'too-many-files':
            setError(t('uploadImage.tooManyFiles'));
            break;

          default:
            setError(t('errors.unknown'));
        }
      },
      [t],
    ),
  });

  const submit = async () => {
    if (!file) {
      return;
    }

    setLoading(true);
    lock('image.upload', () => false);

    try {
      await save(file);
      close('image.upload');
    } catch (err) {
      setLoading(false);
      lock('image.upload', () => true);

      if (onSaveError) {
        onSaveError(err);
      } else if (err instanceof CommonError) {
        setError(t(err.messageKey));
      } else {
        setError(t('errors.unknown'));
        handleError(err, 'UNHANDLED_RESPONSE');
      }
    }
  };

  return (
    <GenericModal sx={styles.modal}>
      <Typography variant="h5" textAlign="center">
        {t(title)}
      </Typography>

      <Box {...getRootProps()} sx={styles.dropZone}>
        <input {...getInputProps()} />

        {file ? (
          <UploadImageSelected file={file} clear={() => setFile(null)} />
        ) : (
          <UploadImagePlaceholder select={() => open()} />
        )}

        <Fade in={isDragActive}>
          <Box sx={styles.dropZoneOverlay}>
            <Typography variant="body2">{t('uploadImage.dropHere')}</Typography>
          </Box>
        </Fade>
      </Box>

      <FormHelperText error>{error || <ReservedLines />}</FormHelperText>

      <Box sx={styles.footer}>
        <Button variant="outlined" onClick={() => close('image.upload')} disabled={loading}>
          {t('common.cancel')}
        </Button>

        <Button disabled={loading || !file} onClick={submit}>
          {t('common.save')}
          {loading && <CircularProgress />}
        </Button>
      </Box>
    </GenericModal>
  );
};
